import { differenceInDays } from 'date-fns'

/**
 * Compares two dates
 * @param a
 * @param b
 * @returns
 * - a < b => '-1';
 * - a = b => '0'
 * - a > b => '1'
 */
export function compareDates(a: Date, b: Date, includeTimePart?: boolean): number {
  if (a.getFullYear() > b.getFullYear()) {
    return 1
  }
  if (a.getFullYear() < b.getFullYear()) {
    return -1
  }
  if (a.getMonth() > b.getMonth()) {
    return 1
  }
  if (a.getMonth() < b.getMonth()) {
    return -1
  }
  if (a.getDate() > b.getDate()) {
    return 1
  }
  if (a.getDate() < b.getDate()) {
    return -1
  }
  if (includeTimePart) {
    if (a.getHours() > b.getHours()) {
      return 1
    }
    if (a.getHours() < b.getHours()) {
      return -1
    }
    if (a.getMinutes() > b.getMinutes()) {
      return 1
    }
    if (a.getMinutes() < b.getMinutes()) {
      return -1
    }
    if (a.getSeconds() > b.getSeconds()) {
      return 1
    }
    if (a.getSeconds() < b.getSeconds()) {
      return -1
    }
  }
  return 0
}

export function dateIsExpired(a: Date) {
  return compareDates(a, new Date()) < 0
}

export function dateAtStartOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate())
}

export function todayStartOfTheDay(): Date {
  return dateAtStartOfDay(new Date())
}

export function addDays(d: Date, days: number): Date {
  const calcDate = new Date(d.setDate(d.getDate() + days))
  return calcDate
}

export function addYears(d: Date, years: number): Date {
  return addDays(d, years * 365)
}

export function tomorrowStartOfDay(): Date {
  return dateAtStartOfDay(addDays(new Date(), 1))
}

export function yesterdayStartOfDay(): Date {
  return dateAtStartOfDay(addDays(new Date(), -1))
}

export function isValidDate(date?: Date | null): date is Date {
  return date !== undefined && date !== null && date instanceof Date && !isNaN(date.getTime())
}

/** Differences in days between two dates provided. Ex. 2024-10-10; 2024-10-15 => 5 */
export function getDayDiff(start: Date, end: Date): number {
  return differenceInDays(end, start)
}

export function dateEquality(firstDate: Date, secondDate: Date) {
  return firstDate.getFullYear() === secondDate.getFullYear() && firstDate.getMonth() === secondDate.getMonth() && firstDate.getDate() === secondDate.getDate()
}

// Alias name for the function getDayDiff(startDate, endDate)
export function getNights(startDate: Date, endDate: Date): number {
  return getDayDiff(startDate, endDate)
}

/** returns an array with all the dates of the days between the two dates provided */
export function getNightsInRange(startDate: Date, endDate: Date, includeEndDate?: boolean): Date[] {
  const date = new Date(startDate.getTime())

  const dates: Date[] = []

  while (date < endDate) {
    dates.push(new Date(date))
    date.setDate(date.getDate() + 1)
  }
  if (includeEndDate) {
    dates.push(new Date(endDate))
  }

  return dates
}

export function formatYYYYmmDD(date: Date) {
  const d = new Date(date)
  let month = String(d.getMonth() + 1)
  let day = String(d.getDate())
  const year = String(d.getFullYear())

  if (month.length < 2) month = '0' + month
  if (day.length < 2) day = '0' + day

  return [year, month, day].join('-')
}

export function getDateWithOffset(date: Date, daysOffset: number) {
  const newDate = new Date(date.getTime())
  newDate.setDate(date.getDate() + daysOffset)
  return newDate
}

export function getLastMonthInitialDate(date: Date) {
  return getDateWithOffset(date, -30)
}

export function isDateBetween(date: Date, start: Date, end: Date, excludeStart?: boolean, excludeEnd?: boolean, includeTimePart?: boolean): boolean {
  const startCompare = compareDates(start, date, includeTimePart)
  const endCompare = compareDates(date, end, includeTimePart)
  const s = excludeStart ? startCompare < 0 : startCompare <= 0
  const e = excludeEnd ? endCompare < 0 : endCompare <= 0
  return s && e
}

const DAY_MS = 86400000
export function daysArrayISO(startIso: Date, endIso: Date) {
  const arr = []
  for (let loop_epoch = startIso.getTime(); loop_epoch <= endIso.getTime(); loop_epoch += DAY_MS) {
    const loop_iso = convertToShortISO(new Date(loop_epoch))
    arr.push(loop_iso)
  }
  return arr
}

export function convertToShortISO(date?: Date) {
  if (!date) return ''
  const isoArray = date.toLocaleDateString('en-GB').split('/')
  return `${isoArray[2]}-${isoArray[1]}-${isoArray[0]}`
}

/**
 * Check if the difference between the two dates is less than one day, if true, dates are considered as contiguous.
 * @param a
 * @param b
 * @param ignoreADay if provided and difference is equal to a day, the two dates will not be considered contiguous
 * @returns
 */
export const areContiguousDays = (a: Date, b: Date, ignoreADay?: boolean) => {
  const d1 = new Date(Date.UTC(a.getFullYear(), a.getMonth(), a.getDate()))
  const d2 = new Date(Date.UTC(b.getFullYear(), b.getMonth(), b.getDate()))
  const diff = +d2 - +d1
  return diff >= 0 && (ignoreADay ? diff < DAY_MS : diff <= DAY_MS)
}

export function convertTimeZone(date: Date, tzString?: string) {
  return new Date(date.toLocaleString('en-US', { timeZone: tzString ?? 'Europe/Rome' }))
}

export function getTimeOnlyInMinutes(date: Date) {
  const hoursInMinutes = date.getUTCHours() * 60
  const minutes = date.getUTCMinutes()
  return hoursInMinutes + minutes
}

export function joinDateAndTime(date: Date, time: Date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate(), time.getHours(), time.getMinutes())
}

/**
 * Return a date at midnight yyyy-MM-ddT00:00:00.000Z (as Date)
 */
export function getDatePartOnly(date: Date) {
  return dateAtStartOfDay(date)
}

/**
 * Return time part of the date provided
 * The result will be a Date on 1970-01-01 with the time of original date.
 */
export function getTimePartOnly(date: Date) {
  return new Date(1970, 0, 1, date.getHours(), date.getMinutes(), date.getSeconds(), date.getMilliseconds())
}

/**
 * Check if the date provided is in the age range (minAge < birthDate <= maxAge)
 * @param birthDate
 * @param minAge
 * @param maxAge
 * @param today If not specified the reference date is today
 * @returns
 */
export function isInAgeRange(birthDate: Date, minAge: number, maxAge: number, today?: Date) {
  const referenceDate = today ?? new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate(), 0, 0, 0)
  const minAgeDate = new Date(referenceDate.getFullYear() - minAge, referenceDate.getMonth(), referenceDate.getDate(), 0, 0, 0)
  const maxAgeDate = new Date(referenceDate.getFullYear() - maxAge, referenceDate.getMonth(), referenceDate.getDate(), 0, 0, 0)
  return isDateBetween(birthDate, maxAgeDate, minAgeDate)
}
